import { useEffect, useState } from 'react'
import styled from 'styled-components'
import { resolveMatch, moveIcon } from '../roshambo'
import { getUserLabel } from './user-label'

const Card = styled.div`
  ${({ theme: { colors, monoFont } }) => `
    background: ${colors('shadow', 0.5)};
    border: 1px dashed ${colors('foreground', 0.2)};
    color: ${colors('text')};
    padding: 0.5rem 0.75rem;
    font-family: ${monoFont};
    font-size: 0.75rem;
    max-width: 90%;
    align-self: center;
    overflow-anchor: none;
    text-align: center;
  `}
`

const Moves = styled.div`
  font-size: 1.1rem;
  margin: 0.2rem 0;
`

// Renders the single, evolving card for one Roshambo match. Every protocol
// message for a matchId collapses into this one card (see panel.js) — the
// card's content just reflects whatever phase buildMatches() currently
// reports.
export const RoshamboCard = ({ match }) => {
  const [result, setResult] = useState({ phase: 'open' })

  useEffect(() => {
    let cancelled = false
    resolveMatch(match).then((r) => {
      if (!cancelled) setResult(r)
    })
    return () => {
      cancelled = true
    }
  }, [match])

  const challengerName = getUserLabel(match.challenge.User)
  const opponentName = match.accept ? getUserLabel(match.accept.User) : null

  if (result.phase === 'open') {
    const text = match.target
      ? `🪨📄✂️ ${challengerName} challenged ${match.targetName} to Roshambo — type /roshambo join to accept!`
      : `🪨📄✂️ ${challengerName} is looking for a Roshambo opponent — type /roshambo join`
    return <Card>{text}</Card>
  }

  if (result.phase === 'choosing') {
    const challengerReady = match.commits[result.challengerId] ? '✅' : '⏳'
    const opponentReady = match.commits[result.opponentId] ? '✅' : '⏳'
    return (
      <Card>
        {`⚔️ ${challengerName} vs ${opponentName} — pick your move with /roshambo rock|paper|scissors`}
        <Moves>{`${challengerReady} ${challengerName}    ${opponentReady} ${opponentName}`}</Moves>
      </Card>
    )
  }

  if (result.phase === 'revealing') {
    return <Card>{`⚔️ ${challengerName} vs ${opponentName} — both locked in, revealing…`}</Card>
  }

  if (result.phase === 'complete') {
    const challengerMove = result.challengerMove
      ? `${moveIcon(result.challengerMove)} ${result.challengerMove}`
      : '⚠️ invalid move'
    const opponentMove = result.opponentMove
      ? `${moveIcon(result.opponentMove)} ${result.opponentMove}`
      : '⚠️ invalid move'

    let verdict = '🤝 It\'s a tie!'
    if (result.outcome === 'challenger') verdict = `🏆 ${challengerName} wins!`
    if (result.outcome === 'opponent') verdict = `🏆 ${opponentName} wins!`
    if (result.outcome === 'void') verdict = 'no contest — both moves were invalid'

    return (
      <Card>
        {`⚔️ ${challengerName} vs ${opponentName}`}
        <Moves>{`${challengerName}: ${challengerMove}    ${opponentName}: ${opponentMove}`}</Moves>
        {verdict}
      </Card>
    )
  }

  return null
}
